import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  instituteTypeFormSchema,
  type CreateInstituteTypePayload,
  type InstituteType,
  type InstituteTypeFormValues,
  type UpdateInstituteTypePayload,
} from '../types/instituteType.types';
import { useCreateInstituteType } from './useCreateInstituteType';
import { useUpdateInstituteType } from './useUpdateInstituteType';

interface UseInstituteTypeFormParams {
  instituteType: InstituteType | null;
  open: boolean;
  onClose: () => void;
}

const EMPTY_DEFAULTS: InstituteTypeFormValues = {
  name: '',
  code: '',
  isActive: true,
  requiresEiin: false,
};

function toFormValues(instituteType: InstituteType | null): InstituteTypeFormValues {
  if (!instituteType) return EMPTY_DEFAULTS;
  return {
    name: instituteType.name,
    code: instituteType.code ?? '',
    isActive: instituteType.isActive,
    requiresEiin: instituteType.requiresEiin,
  };
}

function buildCreatePayload(values: InstituteTypeFormValues): CreateInstituteTypePayload {
  const trimmedCode = values.code.trim();
  const payload: CreateInstituteTypePayload = {
    name: values.name,
    isActive: values.isActive,
    requiresEiin: values.requiresEiin,
  };
  if (trimmedCode.length > 0) {
    payload.code = trimmedCode;
  }
  return payload;
}

export function useInstituteTypeForm({ instituteType, open, onClose }: UseInstituteTypeFormParams) {
  const isEditMode = instituteType !== null;
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const form = useForm<InstituteTypeFormValues>({
    resolver: zodResolver(instituteTypeFormSchema),
    defaultValues: toFormValues(instituteType),
  });

  useEffect(() => {
    if (open) {
      form.reset(toFormValues(instituteType));
    } else {
      form.reset(EMPTY_DEFAULTS);
    }
  }, [instituteType, open, form]);

  const createMutation = useCreateInstituteType(onClose);
  const updateMutation = useUpdateInstituteType(onClose);

  const isPending = isEditMode ? updateMutation.isPending : createMutation.isPending;

  function submit(values: InstituteTypeFormValues) {
    if (isEditMode && instituteType) {
      const changedKeys = Object.keys(
        form.formState.dirtyFields,
      ) as (keyof InstituteTypeFormValues)[];
      if (changedKeys.length === 0) {
        onClose();
        return;
      }
      const updatePayload: UpdateInstituteTypePayload = Object.fromEntries(
        changedKeys.map((key) => [key, values[key]]),
      );
      updateMutation.mutate({ id: instituteType.id, payload: updatePayload });
      return;
    }
    createMutation.mutate(buildCreatePayload(values));
  }

  function requestClose() {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }
    setIsDiscardDialogOpen(true);
  }

  function confirmDiscard() {
    setIsDiscardDialogOpen(false);
    onClose();
  }

  function cancelDiscard() {
    setIsDiscardDialogOpen(false);
  }

  return {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  };
}
